<script setup lang="ts">
import { computed } from 'vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { formatNumberFullTruncate } from '@/helpers/utils'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import GoldenWheelOverlayBackground from '@/assets/images/golden-wheel-overlay-background.png'
import type { StepReward } from '@/types/shared/pathway-event'
import { SPECIAL_CURRENCY_ID } from '@/types/shared/player'

// Import special currency images
import { usePlayer } from '@/composables/game/player'
import { specialCurrenciesConfig } from '@/config/special-currencies'

const props = defineProps<{
	show: boolean
	totalRewards: Partial<StepReward>
}>()

const emit = defineEmits<{
	(e: 'hide'): void
}>()

const { topNumber } = useFullScreenDimensions()
const { goldenCoinValue } = usePlayer()

useWatchOverlays(computed(() => props.show))

// Map of special currency images
const specialCurrencyImageMap: Record<SPECIAL_CURRENCY_ID, string> = {
  [SPECIAL_CURRENCY_ID.GEM]: specialCurrenciesConfig.gem.image,
  [SPECIAL_CURRENCY_ID.CROWN]: specialCurrenciesConfig.crown.image,
  [SPECIAL_CURRENCY_ID.GOLDEN_EGG]: specialCurrenciesConfig.goldenEgg.image,
  [SPECIAL_CURRENCY_ID.KEY]: specialCurrenciesConfig.key.image,
  [SPECIAL_CURRENCY_ID.PARACHUTE]: specialCurrenciesConfig.parachute.image,
  [SPECIAL_CURRENCY_ID.PLANT]: specialCurrenciesConfig.plant.image,
  [SPECIAL_CURRENCY_ID.TOP_HAT]: specialCurrenciesConfig.topHat.image,
  [SPECIAL_CURRENCY_ID.GOLD_FEATHER]: specialCurrenciesConfig.goldFeather.image2,
  [SPECIAL_CURRENCY_ID.HOLIDAY_ORB]: specialCurrenciesConfig.holidayOrb.image,
}

// Get all special currencies with values > 0
const specialCurrenciesToDisplay = computed(() => {
  if (!props.totalRewards.specialCurrencies) return []

  return Object.entries(props.totalRewards.specialCurrencies)
    .filter(([, amount]) => amount && amount > 0)
    .map(([currencyId, amount]) => ({
      id: currencyId as SPECIAL_CURRENCY_ID,
      amount: amount!,
      image: specialCurrencyImageMap[currencyId as SPECIAL_CURRENCY_ID],
    }))
})

function handleClose() {
  emit('hide')
}
</script>

<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="dro-overlay fixed max-h-screen h-screen w-screen left-0 bottom-0 bg-dark-most-bottom-layer/[.66] backdrop-blur-[4px] flex flex-col z-[950] px-4 normal-zeros"
      :style="{
        paddingTop: `${topNumber + 24}px`,
      }"
    >
      <div class="h-full w-full flex-1 flex flex-col relative">
        <img
          :src="GoldenWheelOverlayBackground"
          class="absolute top-1/3 left-1/2 -translate-x-1/2 blur-md scale-[1.3] -z-[2]"
        >
        <div
          class="absolute w-[334px] h-[334px] rounded-full bg-green-primary/[.54] blur-[150px] -z-[1]"
        />

        <!-- Close button at top -->
        <div class="flex items-start justify-end z-[1] mb-4">
          <CrossCircleIcon
            class="size-8 min-w-[32px] text-white/[87%]"
            @click="handleClose"
          />
        </div>

        <!-- Content centered -->
        <div class="flex-1 flex items-center justify-center">
          <div class="text-center px-4 max-w-sm w-full">
            <h2 class="text-4xl font-bold text-white mb-3">
              Yay, puzzle solved!
            </h2>
            <p class="text-2xl text-white/90 mb-12 font-semibold">
              Congrats, you receive:
            </p>

            <!-- Rewards Display -->
            <div class="flex items-center justify-center gap-6 mb-12 flex-wrap">
              <!-- Golden Coins -->
              <div
                v-if="totalRewards.goldenCoins && totalRewards.goldenCoins > 0"
                class="flex flex-col items-center"
              >
                <CoinIcon class="w-auto h-16 -mb-6" />
                <span
                  class="text-white text-[45px] leading-[60px] font-bold gray-text-stroke"
                >{{ formatNumberFullTruncate(totalRewards.goldenCoins * goldenCoinValue)
                }}</span>
              </div>
              <!-- Special Currencies -->
              <div
                v-for="currency in specialCurrenciesToDisplay"
                :key="currency.id"
                class="flex flex-col items-center"
              >
                <img
                  :src="currency.image"
                  class="w-auto h-16 -mb-6 object-contain"
                  :alt="currency.id"
                >
                <span
                  class="text-white text-[45px] leading-[60px] font-bold gray-text-stroke"
                >{{ formatNumberFullTruncate(currency.amount)
                }}</span>
              </div>
              <!-- Spins -->
              <div
                v-if="totalRewards.spins && totalRewards.spins > 0"
                class="flex flex-col items-center"
              >
                <img
                  src="@/assets/images/fortune-wheel-without-border.png"
                  class="w-auto h-16 -mb-6 object-contain"
                  alt="Spins"
                >
                <span
                  class="text-white text-[45px] leading-[60px] font-bold gray-text-stroke"
                >{{ formatNumberFullTruncate(totalRewards.spins)
                }}</span>
              </div>
              <!-- Dropee Stars -->
              <div
                v-if="totalRewards.dropeeStars && totalRewards.dropeeStars > 0"
                class="flex flex-col items-center"
              >
                <img
                  src="@/assets/images/premium.png"
                  class="w-auto h-16 -mb-6 object-contain"
                  alt="Dropee Stars"
                >
                <span
                  class="text-white text-[45px] leading-[60px] font-bold gray-text-stroke"
                >{{ formatNumberFullTruncate(totalRewards.dropeeStars)
                }}</span>
              </div>
              <!-- Tickets -->
              <div
                v-if="totalRewards.giveawayTickets && totalRewards.giveawayTickets > 0"
                class="flex flex-col items-center"
              >
                <img
                  src="@/assets/images/emojis/emoji-ticket.png"
                  class="w-auto h-16 -mb-6 object-contain"
                  alt="Tickets"
                >
                <span
                  class="text-white text-[45px] leading-[60px] font-bold gray-text-stroke"
                >{{ formatNumberFullTruncate(totalRewards.giveawayTickets)
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.black-stroke {
	-webkit-text-stroke-width: 5px;
	-webkit-text-stroke-color: #000;
	text-shadow: 0px 0px 5px rgba(0, 0, 0, 0.95),
	0px 0px 10px rgba(0, 0, 0, 0.85),
	0px 0px 15px rgba(0, 0, 0, 0.75),
	0px 0px 20px rgba(0, 0, 0, 0.65);
	paint-order: stroke fill;
}

.gray-text-stroke {
	-webkit-text-stroke-width: 5px;
	-webkit-text-stroke-color: #3A3A3C;
	text-shadow: 0 1.457px 2.915px rgba(0, 0, 0, 0.32);
	paint-order: stroke fill;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.normal-zeros {
	padding-bottom: env(safe-area-inset-bottom);
}
</style>